import { Component, Input, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormComponent } from '../../base/form.component';
import { HttpClient } from '@angular/common/http';
import { catchError, debounceTime, distinctUntilChanged, Observable, of, switchMap, tap } from 'rxjs';
import { Address } from '../../models/address/address.model';

@Component({
    selector: 'tvcore-auto-complete-address',
    templateUrl: './auto-complete-address.component.html',
    styleUrls: ['./auto-complete-address.component.scss'],
    standalone: false
})
export class AutoCompleteAddressComponent extends FormComponent implements OnInit {
	@Input()
	declare form: FormGroup;
	@Input()
	declare showErrors: boolean;
	@Input()
	TRANSLATIONS: any;

	addresses$: Observable<Address[]>;
	status: 'loading' | 'idle' | 'selected' = 'selected';

	constructor(private http: HttpClient) {
		super();
	}

	ngOnInit() {
		this.addresses$ = this.form.valueChanges.pipe(
			distinctUntilChanged((prev, curr) => this.isSameAddress(prev, curr)),
			tap(() => (this.status = 'loading')),
			debounceTime(400),
			switchMap((value) => this.suggestAddress(value.street, value.postalCode, value.cityName)),
			tap((addresses) => {
				this.status = 'idle';
				this.checkResultsForAddress(addresses);
			}),
			catchError(() => of([]))
		);
	}

	selectAddress(address: Address) {
		const data: any = {
			postalCode: address.postalCode,
			cityName: address.cityName,
		};

		if (address.street) {
			data.street = address.street;
		}

		this.form.patchValue(data, { emitEvent: false });
		this.status = 'selected';
	}

	override isFormValid(triggerErrors: boolean = true): boolean {
		const formValid = super.isFormValid(triggerErrors);

		return formValid && this.status === 'selected';
	}

	private checkResultsForAddress(addresses: Address[]) {
		if (addresses?.length) {
			const { street, postalCode, cityName } = this.form.value;

			let found = addresses.find((a) => a.street?.toLowerCase() === street?.toLowerCase() && a.postalCode === postalCode && a.cityName?.toLowerCase() === cityName?.toLowerCase());

			if (!found) {
				found = addresses.find((a) => a.street === undefined && !street && a.postalCode === postalCode && a.cityName?.toLowerCase() === cityName?.toLowerCase());
			}

			if (found) {
				this.selectAddress(found);
			}
		}
	}

	private isSameAddress(a: Address | any, b: Address | any): boolean {
		return a.street === b.street && a.postalCode === b.postalCode && a.cityName === b.cityName;
	}

	private suggestAddress(street: string, postalCode: string, cityName: string): Observable<Address[]> {
		const params = {
			street: street || '',
			postalCode: postalCode || '',
			cityName: cityName || '',
		};

		return this.http.get<Address[]>(`/rest/1.0/address/suggestion`, {
			params,
		});
	}
}
